import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { ChevronPointedDownIcon, ChevronPointedUpIcon } from 'assets/icons';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { SidePanelOrganizationPetFragment, SpeciesType } from 'shared/types/graphql';
import { getSubscriptionPaymentStatus, isEnrollmentConsideredActive } from '../../utils/careEnrollments';

export enum SelectorDirection {
  Up,
  Down,
}
interface IPetSelectorListItemProps {
  petName: string;
  species: string;
  speciesType: SpeciesType;
  photoUrl: string;
  showSelector?: boolean;
  selectorDirection?: SelectorDirection;
  isDeceased?: boolean;
  isActive?: boolean;
  organizationPet?: SidePanelOrganizationPetFragment | null;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}
export const PetSelectorListItem = ({
  petName,
  showSelector,
  selectorDirection,
  species,
  speciesType,
  photoUrl,
  isDeceased,
  isActive,
  organizationPet,
  ...rest
}: IPetSelectorListItemProps): JSX.Element => {
  const enrollment = useMemo(
    () => organizationPet?.carePlanEnrollments?.find((enrollment) => isEnrollmentConsideredActive(enrollment)),
    [organizationPet?.carePlanEnrollments],
  );
  const careSubscriptionPaymentStatus = useMemo(() => getSubscriptionPaymentStatus(organizationPet), [organizationPet]);
  const planTitle = enrollment?.plan.flowAlias || enrollment?.plan.title || '';
  const isPreviousMember = !enrollment && (isDeceased || !isActive);
  return (
    <>
      <DevModeDisplayJSON
        src={{
          __typename: 'PetStub',
          petName,
          planTitle,
          showSelector,
          selectorDirection,
          species,
          speciesType
        }}
      />
      <Container {...rest}>
        <ItemInfo>
          <PetAvatarWrapper>
            <PetAvatar
              speciesType={speciesType}
              name={petName}
              photoUrl={photoUrl}
              isDeceased={isDeceased}
              isActive={isActive}
              size="md"
              careStatus={careSubscriptionPaymentStatus}
            />
          </PetAvatarWrapper>
          <PetInfo>
            <Text as="p" isTruncated size="lg" fontWeight="bold">
              {petName}
            </Text>
            <PlanHeading>
              {!!planTitle ? (
                <Text size="sm">{planTitle}</Text>
              ) : (
                <Text variant="subtle" size="sm" fontStyle="italic">
                  {isPreviousMember ? 'Previous Member' : 'No Membership'}
                </Text>
              )}
            </PlanHeading>
          </PetInfo>
        </ItemInfo>
        {showSelector && (
          <DropDownButton>
            {selectorDirection === SelectorDirection.Down ? <ChevronPointedDownIcon /> : <ChevronPointedUpIcon />}
          </DropDownButton>
        )}
      </Container>
    </>
  );
};

const ItemInfo = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  display: contents;
`;
const DropDownButton = styled.div`
  text-align: center;
  cursor: pointer;
  margin-top: 20px;
`;

const PetInfo = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  word-wrap: break-word;
  flex-shrink: 1;
`;
const Container = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  max-width: 300px;
  &:not(:last-child) {
    border-bottom: 1px solid #d2d2d2;
  }
`;

const PlanHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`;

const PetAvatarWrapper = styled.div`
  display: flex;
  margin: auto;
`;
